import { Alert, Box, Button } from "@mui/material";
import { COLORS, primaryContainedButtonSx } from "assets";
import { ActivationModelGrid, BulkOverrideConfirm, MyModal } from "components";
import { ENDPOINTS, filterModelsForUI, normalizeActiveModelMedia, UI_MODELS } from "config/constants";
import { useFetch } from "hooks";
import useTranslation from "hooks/useTranslation";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ActiveModel, CameraModel, ModelTypes } from "types";

interface BulkManageCamerasProps {
  cameras: CameraModel[];
  enabledModels: ModelTypes[];
  onSuccess: () => void;
  onClose: () => void;
}

function modelsFingerprint(camera: CameraModel) {
  const list = [...(camera.activeModels || [])]
    .map((m) => `${m.model}:${m.video}:${m.audio}`)
    .sort()
    .join("|");
  return list;
}

function allCamerasMatch(cameras: CameraModel[]) {
  if (!cameras.length) return false;
  const first = modelsFingerprint(cameras[0]);
  return cameras.every((camera) => modelsFingerprint(camera) === first);
}

function cloneActiveModels(camera: CameraModel): ActiveModel[] {
  return (camera.activeModels || []).map((m) => {
    const flags = normalizeActiveModelMedia(m.model, Boolean(m.video), Boolean(m.audio));
    return { model: m.model, ...flags };
  });
}

export default function BulkManageCameras({ cameras, enabledModels, onSuccess, onClose }: BulkManageCamerasProps) {
  const t = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [activeModels, setActiveModels] = useState<ActiveModel[]>(() =>
    allCamerasMatch(cameras) ? cloneActiveModels(cameras[0]) : []
  );
  const { isLoading, response, error, callFetch } = useFetch<{ updated: string[]; failed: { id: string }[] }>("PUT");

  const mixed = useMemo(() => cameras.length >= 2 && !allCamerasMatch(cameras), [cameras]);

  const models = enabledModels.length ? filterModelsForUI(enabledModels) : [...UI_MODELS];

  const onChangeActiveModels = useCallback((model: ModelTypes, video: boolean, audio: boolean) => {
    const flags = normalizeActiveModelMedia(model, video, audio);
    setActiveModels((prev) => {
      const without = prev.filter((item) => item.model !== model);
      if (!flags.video && !flags.audio) return without;
      return [...without, { model, ...flags }];
    });
  }, []);

  const onRequestSave = useCallback(() => {
    if (!activeModels.length) {
      enqueueSnackbar(t["At least one activation model is required."] || "At least one activation model is required.", {
        variant: "error"
      });
      return;
    }
    setConfirmOpen(true);
  }, [activeModels.length, enqueueSnackbar, t]);

  const onConfirmSave = useCallback(() => {
    callFetch(ENDPOINTS.cameras + "/bulk", {
      body: JSON.stringify({
        ids: cameras.map((c) => c._id),
        activeModels
      })
    }).catch(console.error);
  }, [activeModels, callFetch, cameras]);

  useEffect(() => {
    if (response) {
      const failed = response.data?.failed || [];
      enqueueSnackbar(response.message || "", { variant: failed.length ? "warning" : "success" });
      if (failed.length) {
        const names = failed
          .map((f) => {
            const cam = cameras.find((c) => c._id === f.id);
            return cam?.name || cam?.cameraId || f.id;
          })
          .join(", ");
        enqueueSnackbar(`Failed: ${names}`, { variant: "error" });
      }
      setConfirmOpen(false);
      onSuccess();
      onClose();
    }
  }, [response, enqueueSnackbar, onSuccess, onClose, cameras]);

  useEffect(() => {
    if (error) enqueueSnackbar(error.message || "", { variant: "error" });
  }, [error, enqueueSnackbar]);

  return (
    <>
      <MyModal
        title={`${t["Manage Models"] || "Manage Models"} — ${cameras.length} ${
          cameras.length === 1 ? "Camera" : "Cameras"
        }`}
        subtitle={
          t["Configure detection models and media processing for all selected cameras"] ||
          "Configure detection models and media processing for all selected cameras"
        }
        open={true}
        setOpen={onClose}
        variant="light"
        width={640}
      >
        {mixed ? (
          <Alert severity="warning" sx={{ mb: 2.5 }}>
            {t["Selected cameras currently use different settings. Saving will override all of them with the values below."] ||
              "Selected cameras currently use different settings. Saving will override all of them with the values below."}
          </Alert>
        ) : null}

        <ActivationModelGrid
          enabledModels={models}
          activeModels={activeModels}
          onChange={onChangeActiveModels}
          title={t["Activation Models"] || "Activation Models"}
          description={
            t["Activation Models description"] || "Select models and media processing for the selected cameras."
          }
          hint={t["At least one activation model is required."] || "At least one activation model is required."}
          videoLabel={t["Video"] || "Video"}
          audioLabel={t["Audio"] || "Audio"}
          mediaProcessingLabel={t["Media Processing"] || "Media Processing"}
          t={t}
        />

        <Box display="flex" justifyContent="flex-end" gap={1.5} mt={3}>
          <Button
            type="button"
            variant="outlined"
            onClick={onClose}
            disabled={isLoading}
            sx={{
              minWidth: 120,
              height: 44,
              borderRadius: `${COLORS.radius.md}px`,
              borderColor: COLORS.surface.border,
              color: COLORS.surface.primaryText,
              fontWeight: 500,
              textTransform: "none"
            }}
          >
            {t["Cancel"]}
          </Button>
          <Button
            type="button"
            variant="contained"
            color="primary"
            disabled={isLoading || !activeModels.length}
            onClick={onRequestSave}
            sx={{
              minWidth: 140,
              height: 44,
              borderRadius: `${COLORS.radius.md}px`,
              fontWeight: 500,
              textTransform: "none",
              ...primaryContainedButtonSx
            }}
          >
            {t["Save Changes"] || "Save Changes"}
          </Button>
        </Box>
      </MyModal>

      <BulkOverrideConfirm
        open={confirmOpen}
        count={cameras.length}
        entityLabel={cameras.length === 1 ? "camera" : "cameras"}
        mixed={mixed}
        isLoading={isLoading}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={onConfirmSave}
      />
    </>
  );
}
